<script setup lang="ts">
// @ts-ignore
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import { Options } from '@splidejs/splide'
import type { DropdownOption } from '~/components/form/select/simple/FormSelectSimple.vue'

export type TabsType = 'primary' | 'secondary' | 'tertiary' | 'quarternary'
export type TabsDirection = 'horizontal' | 'vertical'

export interface TabLink {
    name: string
    id?: number | string
    count?: number
    icon?: string
    slug?: string
}
export interface TabsProps {
    links: Array<TabLink>
    linkClass?: string
    type?: TabsType
    addclas?: string
    selectOnMobile?: boolean
    selectLabel?: string
    direction?: TabsDirection
    tabsId: string
    isSlider?: boolean
    scrollOffset?: number
}
export interface TabsEmits {
    // eslint-disable-next-line no-unused-vars
    (e: 'click', value: number): void
}

const props = withDefaults(defineProps<TabsProps>(), {
    type: 'primary',
    linkClass: '',
    addclas: '',
    selectOnMobile: false,
    direction: 'horizontal',
    selectLabel: undefined,
    scrollOffset: 100,
})
const { $t } = useNuxtApp()
const activeTab = ref<number>(0)
const emits = defineEmits<TabsEmits>()
const tabsRef = ref<HTMLElement>()
const tabsLinksRef = ref<HTMLElement[]>()
const showAllBtn = ref<boolean>(false)
const showAllTabs = ref<boolean>(false)
const selectedDropdownValue = ref<string>('0|0')

const currentDropdownOption = computed((): DropdownOption => {
    const [, index] = selectedDropdownValue.value.split('|')
    const idx = Number(index)
    if (idx >= 0 && idx < props.links.length) {
        return {
            name: props.links[idx]!.name,
            value: selectedDropdownValue.value
        }
    }
    return {
        name: props.links[0]?.name || '',
        value: '0|0'
    }
})

// Watch activeTab changes to sync dropdown value
watch(activeTab, (newIndex) => {
    if (props.selectOnMobile && props.links[newIndex]) {
        const selectedLink = props.links[newIndex]
        selectedDropdownValue.value = `${selectedLink.id || newIndex}|${newIndex}`
    }
})

const sliderOptions = reactive<Options>({
    autoWidth: true,
    pagination: false,
    arrows: false,
    gap: '2rem',
    padding: '3rem',
    drag: false,
    breakpoints: {
        575: {
            drag: 'free',
            arrows: true
        },
    },
})

const { data, sendEvent } = useTracker()

// Accessibility: Track tab changes for screen reader announcements (WCAG 4.1.2)
const tabChangeAnnouncement = ref<string>('')

// Watch for tab changes and announce them to screen readers
watch(activeTab, (newIndex, oldIndex) => {
    if (oldIndex !== undefined && newIndex !== oldIndex && props.links[newIndex]) {
        tabChangeAnnouncement.value = ''
        nextTick(() => {
            tabChangeAnnouncement.value = `${props.links[newIndex]!.name}, ${$t('záložka')} ${newIndex + 1} ${$t('z')} ${props.links.length}`
        })
    }
})

// Generate slug for each link
const getSlugForLink = (link: TabLink): string => {
    if (link.slug) {
        return link.slug
    }
    return useSlug(link.name)
}

// Accessibility: Remove Splide's carousel ARIA attributes that conflict with tablist semantics (WCAG 1.3.1)
// The tablist already provides proper ARIA - Splide's carousel semantics would create conflicts
const handleMounted = (splide: any) => {
    const splideRoot = splide.root
    if (splideRoot) {
        splideRoot.removeAttribute('role')
        splideRoot.removeAttribute('aria-roledescription')
        splideRoot.removeAttribute('aria-label')
    }

    const track = splide.Components.Elements.track
    if (track) {
        track.removeAttribute('role')
        track.removeAttribute('aria-live')
        track.removeAttribute('aria-atomic')
        track.removeAttribute('aria-label')
    }

    // Remove Splide's list role to let the tablist container control semantics
    const list = splide.Components.Elements.list
    if (list) {
        list.removeAttribute('role')
    }

    // Remove carousel semantics from slides - the tab buttons inside provide proper semantics
    splide.Components.Elements.slides.forEach((slide: HTMLElement) => {
        slide.removeAttribute('role')
        slide.removeAttribute('aria-roledescription')
        slide.removeAttribute('aria-label')
        slide.removeAttribute('aria-current')
    })

    // WCAG 2.4.3: Fix focus order by moving arrows after the track in the DOM
    // On mobile (breakpoint 575), arrows are enabled and need correct tab order
    const arrows = splideRoot.querySelector('.splide__arrows')
    if (arrows && track) {
        track.after(arrows)
        // Add accessible labels to arrow buttons
        const prevBtn = arrows.querySelector('.splide__arrow--prev')
        const nextBtn = arrows.querySelector('.splide__arrow--next')
        if (prevBtn) {
            prevBtn.setAttribute('aria-label', $t('Predchádzajúci') as string)
        }
        if (nextBtn) {
            nextBtn.setAttribute('aria-label', $t('Nasledujúci') as string)
        }
    }
}

const handleClickOnTab = (e: Event): void => {
    // get target
    const target = e.target as HTMLElement
    const index = Number(target.dataset.index)
    // set active tab
    activeTab.value = index
    // emit event
    emits('click', Number(target.dataset.id))

    sendEvent({
        data: {
            ...data,
            click: target.dataset.label || '',
        },
    })

    // Update URL hash with the slug without triggering browser navigation
    // This prevents focus from jumping to the hash target (WCAG 2.4.3)
    const slug = getSlugForLink(props.links[index]!)
    history.replaceState(null, '', `#${slug}`)
}

const handleSelectChange = (value: string): void => {
    const [id, index] = value.split('|')
    const indexNum = Number(index)
    activeTab.value = indexNum
    // emit event
    emits('click', Number(id))

    // Update URL hash with the slug without triggering browser navigation
    // This prevents focus from jumping to the hash target (WCAG 2.4.3)
    const slug = getSlugForLink(props.links[indexNum]!)
    history.replaceState(null, '', `#${slug}`)
}

const handleKeyDown = (e: Event):void => {

    const target = e.target as HTMLElement
    const key = (e as KeyboardEvent).key
    const index = Number(target.dataset.index)

    const nextKey = props.direction === 'horizontal' ? 'ArrowRight' : 'ArrowDown'
    const prevKey = props.direction === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'

    if (key === nextKey) {
        e.preventDefault()

        if(tabsLinksRef.value && index === tabsLinksRef.value?.length - 1) {
            activeTab.value = 0
        } else {
            activeTab.value = index + 1
        }
    } else if (key === prevKey) {
        e.preventDefault()

        if (tabsLinksRef.value && index === 0) {
            activeTab.value = tabsLinksRef.value?.length - 1
        } else {
            activeTab.value = index - 1
        }
    }

    tabsLinksRef.value?.[activeTab.value]?.focus()
}

// Handle hash navigation
const handleHashNavigation = ():void => {
    const hash = window.location.hash.substring(1)
    if (hash) {

        const tabIndex = props.links.findIndex((link) =>
            getSlugForLink(link) === hash
        )

        if (tabIndex !== -1) {
            activeTab.value = tabIndex
            emits('click', Number(props.links[tabIndex]!.id || tabIndex))

            nextTick(() => {
                const tabPanel = document.getElementById(`tab-panel-${props.tabsId}-${tabIndex}`)
                if (tabPanel) {
                    const offset = props.scrollOffset
                    const elementPosition = tabPanel.getBoundingClientRect().top
                    const offsetPosition = elementPosition + window.pageYOffset - offset

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    })
                }
            })
        }
    }
}

onMounted(() => {
    if (tabsRef.value) {
        showAllBtn.value =
            tabsRef.value.scrollWidth > tabsRef.value.offsetWidth
    }
    
    // Set initial dropdown value
    if (props.selectOnMobile && props.links.length) {
        const firstLink = props.links[0]!
        selectedDropdownValue.value = `${firstLink.id || 0}|0`
    }
    
    // Check for hash on mount
    handleHashNavigation()
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashNavigation)
})

onUpdated(() => {
    if (tabsRef.value) {
        showAllBtn.value =
            tabsRef.value.scrollWidth > tabsRef.value.offsetWidth
    }
})

onUnmounted(() => {
    // Clean up event listener
    window.removeEventListener('hashchange', handleHashNavigation)
})
</script>
<template>
  <div :class="[`tabs-wrapper-direction-${props.direction}`]">
    <FormSelectSimple
      v-if="props.selectOnMobile"
      id="tabs-select"
      :key="selectedDropdownValue"
      :default-value="currentDropdownOption"
      class="w-100 d-sm-none mb-5"
      position="left"
      :show-label="props.selectLabel !== undefined"
      :label="props.selectLabel"
      type="secondary"
      :options="
        props.links.map((link, index) =>
          Object({
            name: link.name,
            value: `${link.id || index}|${index}`,
          })
        )
      "
      @update="handleSelectChange"
    />

    <div
      v-if="props.direction === 'horizontal' && props.isSlider"
      ref="tabsRef"
      class="tabs"
      :class="[
        `tabs-direction-${props.direction}`,
        `tabs-${props.type}`,
        { 'is-hidden-on-mobile': props.selectOnMobile },
        { 'is-showing-all': showAllTabs },
        {'is-slider': props.isSlider}
      ]"
      role="tablist"
      :aria-orientation="props.direction"
      data-cy="tabs_menu"
    >
      <Splide
        :options="sliderOptions"
        class="tabs-slider position-relative start-50 ps-md-0 translate-middle-x"
        @splide:mounted="handleMounted"
      >
        <SplideSlide
          v-for="(link, index) in props.links"
          :key="`tab-link-${index}`"
          class="mt-6 mt-md-3 tab-list-item flex-shrink-0"
          role="presentation"
        >
          <button
            :id="`tab-${props.tabsId}-${index}`"
            type="button"
            :data-index="index"
            :data-id="link.id || index"
            :data-label="link.name"
            :tabindex="activeTab === index ? 0 : -1"
            role="tab"
            :aria-selected="activeTab === index"
            :aria-controls="`tab-panel-${props.tabsId}-${index}`"
            class="tab-link position-relative transition"
            :class="`${
              activeTab === index ? 'is-active' : ''
            } ${props.linkClass} ${props.addclas}`"
            @keydown="handleKeyDown"
            @click="handleClickOnTab"
          >
            <img
              v-if="link.icon"
              class="d-block tab-link-icon pe-none"
              width="20"
              height="20"
              :src="link.icon"
              alt=""
              loading="lazy"
              decoding="async"
            >
            {{ link.name }}
            <template v-if="link.count">
              ({{ link.count }})
            </template>
            <svg
              v-if="props.type === 'tertiary'"
              class="d-block flex-shrink-0 pe-none transition"
              width="6"
              height="12"
              aria-hidden="true"
            >
              <use xlink:href="#angle-right" />
            </svg>
          </button>
        </SplideSlide>
      </Splide>
    </div>
    <ul
      v-else
      ref="tabsRef"
      class="tabs"
      :class="[
        `tabs-direction-${props.direction}`,
        `tabs-${props.type}`,
        { 'is-hidden-on-mobile': props.selectOnMobile },
        { 'is-showing-all': showAllTabs },
      ]"
      role="tablist"
      :aria-orientation="props.direction"
      data-cy="tabs_menu"
    >
      <!-- Accessibility: Removed role="presentation" from li - the semantic button inside provides proper tab semantics (WCAG 1.3.1) -->
      <li
        v-for="(link, index) in props.links"
        :key="`tab-link-${index}`"
        class="tab-list-item flex-shrink-0"
      >
        <button
          :id="`tab-${props.tabsId}-${index}`"
          ref="tabsLinksRef"
          type="button"
          :data-index="index"
          :tabindex="activeTab === index ? 0 : -1"
          :data-id="link.id || index"
          :data-label="link.name"
          role="tab"
          :aria-selected="activeTab === index"
          :aria-controls="`tab-panel-${props.tabsId}-${index}`"
          class="tab-link position-relative transition"
          :class="`${
            activeTab === index ? 'is-active pe-none' : ''
          } ${props.linkClass} ${props.addclas}`"
          @keydown="handleKeyDown"
          @click="handleClickOnTab"
        >
          <img
            v-if="link.icon"
            class="d-block tab-link-icon pe-none"
            width="20"
            height="20"
            :src="link.icon"
            alt=""
            loading="lazy"
            decoding="async"
          >
          {{ link.name }}
          <template v-if="link.count">
            ({{ link.count }})
          </template>
          <svg
            v-if="props.type === 'tertiary'"
            class="d-block flex-shrink-0 pe-none transition"
            width="6"
            height="12"
            aria-hidden="true"
          >
            <use xlink:href="#angle-right" />
          </svg>
        </button>
      </li>
    </ul>
    <template
      v-for="(link, index) in props.links"
      :key="`tab-panel-${props.tabsId}-${index}`"
    >
      <div
        :id="`tab-panel-${props.tabsId}-${index}`"
        role="tabpanel"
        class="tab-panel"
        :hidden="activeTab !== index"
        :aria-labelledby="`tab-${props.tabsId}-${index}`"
        tabindex="0"
        data-cy="tabs_content"
        :data-slug="getSlugForLink(link)"
      >
        <slot :name="`tab-panel-${index}`" />
      </div>
    </template>
    <!-- Screen reader announcement for tab changes (WCAG 4.1.2) -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ tabChangeAnnouncement }}
    </div>
  </div>
</template>
<style>
@import "./Tabs.vars.scss"
</style>
<style scoped lang="scss">
@import "./Tabs.scss"
</style>